/**
 * PaymentSection Component
 * Section untuk pilih metode pembayaran
 *
 * Supports different payment methods per ship-to:
 * - term_of_payment (TOP): Display only, no selection needed
 * - Others: Show COD and Bank Transfer options
 */

import { Box, Flex, HStack, Icon, Stack } from '@chakra-ui/react';
import { Text } from '@diamond/shared/ui';
import {
  type CheckoutAllowedPaymentMethod,
  type RegularPaymentMethod,
  ESTIMATED_WEIGHT_EMPTY_INTERSECTION_COPY,
} from '@diamond/sol-buyer/utils';
import { AccountBalanceWalletOutlined } from '@mui/icons-material';
import { memo, useCallback, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { EstimatedWeightCheckoutCodNotice } from '../../estimated-weight-product';
import { PaymentOption } from './PaymentOption';

export type AllowedPaymentMethod = CheckoutAllowedPaymentMethod;

export interface PaymentSectionProps {
  selectedPayment?: string;
  /**
   * Payment method allowed for this ship-to
   * - 'term_of_payment': Only show TOP (no selection)
   * - Others: Show COD and Bank Transfer options
   */
  allowedPaymentMethod?: AllowedPaymentMethod;
  /**
   * Regular methods enabled by estimated-weight intersection (or mock COD-only).
   * When omitted, both COD and Bank Transfer remain enabled.
   */
  allowedRegularMethods?: ReadonlyArray<RegularPaymentMethod>;
  /**
   * Show Figma yellow COD notice for produk timbangan.
   */
  showEstimatedWeightNotice?: boolean;
  /**
   * TRD empty-intersection guidance when no shared regular method exists.
   */
  isEmptyIntersection?: boolean;
  onPaymentChange?: (paymentMethod: string) => void;
  embedded?: boolean; // Embedded mode (no border, used in sidebar)
}

/**
 * PaymentSection Component
 * Display payment options based on ship-to's allowed payment method
 *
 * Performance optimizations:
 * - useWatch instead of watch() for isolated field subscription
 * - useCallback for stable handler reference
 * - React.memo prevents re-renders from parent when props unchanged
 */
function PaymentSectionInner({
  selectedPayment,
  allowedPaymentMethod,
  allowedRegularMethods,
  showEstimatedWeightNotice = false,
  isEmptyIntersection = false,
  onPaymentChange,
  embedded = false,
}: PaymentSectionProps) {
  const { register, setValue } = useFormContext();

  // useWatch isolates subscription to specific field only
  const watchedPayment = useWatch({ name: 'payment_method' });
  const currentPayment = watchedPayment || selectedPayment;

  const isTermOfPayment = allowedPaymentMethod === 'term_of_payment';

  const enabledRegularMethods = allowedRegularMethods ?? [
    'cash_on_delivery',
    'bank_transfer',
  ];

  const isCodDisabled = !enabledRegularMethods.includes('cash_on_delivery');
  const isBankTransferDisabled =
    !enabledRegularMethods.includes('bank_transfer');

  /**
   * Auto-set form value to term_of_payment when ship-to requires TOP
   * This ensures the form always has the correct payment method
   */
  useEffect(() => {
    if (isTermOfPayment) {
      setValue('payment_method', 'term_of_payment');
      onPaymentChange?.('term_of_payment');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTermOfPayment]);

  /**
   * Handle payment option change
   * Memoized to prevent child re-renders
   */
  const handlePaymentChange = useCallback(
    (value: string) => {
      if (
        value === 'cash_on_delivery' &&
        !enabledRegularMethods.includes('cash_on_delivery')
      ) {
        return;
      }
      if (
        value === 'bank_transfer' &&
        !enabledRegularMethods.includes('bank_transfer')
      ) {
        return;
      }
      setValue('payment_method', value, { shouldValidate: true });
      onPaymentChange?.(value);
    },
    [setValue, onPaymentChange, enabledRegularMethods]
  );

  /**
   * Term of Payment (TOP) display
   * No selection needed - just display the TOP badge
   */
  const topContent = (
    <Stack spacing={4}>
      {/* Section Title */}
      <Flex align="center" gap={3}>
        <Icon as={AccountBalanceWalletOutlined} color="blue.600" boxSize={6} />
        <Text fontSize="lg" fontWeight="bold" color="gray.900">
          Metode Pembayaran
        </Text>
      </Flex>

      {/* TOP Display */}
      <HStack spacing={4} p={4} bg="gray.50" borderRadius="md">
        <Box>
          <Flex align="center" gap={3}>
            <Text fontSize="2xl" fontWeight="bold" color="gray.800">
              TOP
            </Text>
            <Box
              px={2}
              py={1}
              border="1px"
              borderRadius="lg"
              borderColor="purple.500"
            >
              <Text
                as="i"
                fontWeight="bold"
                bgGradient="linear(to-l, #7928CA, #FF0080)"
                bgClip="text"
                fontSize="xs"
              >
                TERM OF PAYMENT
              </Text>
            </Box>
          </Flex>
          <Text fontSize="sm" fontWeight="medium" color="gray.600" mt={1}>
            Pembayaran sesuai jatuh tempo yang disepakati
          </Text>
        </Box>
      </HStack>
    </Stack>
  );

  /**
   * Regular payment options (COD & Bank Transfer)
   */
  const regularContent = (
    <Stack spacing={4}>
      {/* Section Title */}
      <Flex align="center" gap={3}>
        <Icon as={AccountBalanceWalletOutlined} color="blue.600" boxSize={6} />
        <Text fontSize="lg" fontWeight="bold" color="gray.900">
          Metode Pembayaran
        </Text>
      </Flex>

      {/* Payment Options — notice sits directly above COD (Figma Frame 1597880605) */}
      <Stack spacing={3}>
        {showEstimatedWeightNotice ? (
          <EstimatedWeightCheckoutCodNotice />
        ) : null}

        {/* Cash on Delivery */}
        <PaymentOption
          value="cash_on_delivery"
          title="Cash on Delivery"
          description="Pembayaran setelah barang diterima"
          logoSrc="/images/checkout/logo-cod.svg"
          isSelected={currentPayment === 'cash_on_delivery'}
          isDisabled={isCodDisabled}
          onChange={handlePaymentChange}
          register={register}
        />

        {/* Bank Transfer */}
        <PaymentOption
          value="bank_transfer"
          title="Bank Transfer"
          description="Bayar sekarang, upload bukti transfer, tanpa biaya"
          logoSrc="/images/checkout/logo-bca.png"
          isSelected={currentPayment === 'bank_transfer'}
          isDisabled={isBankTransferDisabled}
          onChange={handlePaymentChange}
          register={register}
        />
      </Stack>

      {isEmptyIntersection ? (
        <Text
          fontSize="sm"
          color="red.600"
          data-testid="estimated-weight-empty-intersection"
        >
          {ESTIMATED_WEIGHT_EMPTY_INTERSECTION_COPY}
        </Text>
      ) : null}
    </Stack>
  );

  // Select content based on payment method type
  const content = isTermOfPayment ? topContent : regularContent;

  // Embedded mode: no border/padding/background
  if (embedded) {
    return content;
  }

  // Standalone mode: with border/padding/background
  return (
    <Box
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="lg"
      p={5}
      bg="white"
    >
      {content}
    </Box>
  );
}

// Memoize to prevent unnecessary re-renders from parent
export const PaymentSection = memo(PaymentSectionInner);
